if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 1024px)');
        this.motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.sliderGallery = this.querySelector('slider-element');
        this.mediaPreview = this.querySelector('.product__preview .product__media');

        this.galleryCard = this.closest('.dd-gallery-card');
        this.badgeEl = this.galleryCard?.querySelector('[data-gallery-badge]');
        this.badgeOptionEl = this.galleryCard?.querySelector('[data-badge-option]');
        this.badgeNicknameEl = this.galleryCard?.querySelector('[data-badge-nickname]');
        this.badgeSepEl = this.galleryCard?.querySelector('.dd-gallery-card__badge-sep');
        this.zoomBtn = this.galleryCard?.querySelector('.dd-gallery-card__zoom button');
        this.colorOptionIndex = parseInt(this.galleryCard?.dataset.colorOptionIndex || '0', 10);
        this.nicknameMap = this._parseNicknameMap(this.galleryCard?.dataset.nicknameMap || '');

        this._variantMediaMap = this._parseVariantMediaMap();

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange.bind(this));
        }

        var productInfo = this.closest('product-info');
        if (productInfo) {
          productInfo.addEventListener('dd:section-render', this._onVariantChange.bind(this));
        }

        if (this.sliderGallery) {
          this.sliderGallery.addEventListener('slider:change', this.onSlideChange.bind(this));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener('click', (e) => {
            const li = e.target.closest('[data-target]');
            if (li) this.setActiveMedia(li.dataset.target, false);
          });
          if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }

        this._blockThumbnailModals();

        this._filterInitialVariant();
        this.countMediaGallery();
        this._sampleZoomContrast();
      }

      onSlideChange(event) {
        const activeMedia = event.detail.currentElement;
        if (!activeMedia) return;

        this.playActiveMedia(activeMedia);
        if (this.mediaPreview) this.updatePreview(activeMedia);

        const viewInSpaceButton = this.querySelector('[data-shopify-xr]');
        if (viewInSpaceButton) {
          if (activeMedia.getAttribute('data-media-type') === 'model') {
            viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'));
          } else {
            viewInSpaceButton.setAttribute(
              'data-shopify-model3d-id',
              viewInSpaceButton.getAttribute('data-shopify-model3d-default-id')
            );
          }
        }

        if (this.elements.thumbnails) {
          const mediaId = activeMedia.dataset.mediaId;
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        if (!this.mediaPreview) this._sampleZoomContrast(activeMedia);
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;

        let activeMedia = viewer.querySelector(`[data-media-id="${mediaId}"]`);
        // Root-cause fix: when a specific mediaId is requested (e.g. injected
        // variant video), do not silently swap to an adjacent slide just because
        // it is currently off-screen in the slider viewport. Only fall back if
        // target media is missing or explicitly hidden.
        if (!activeMedia || activeMedia.hasAttribute('hidden')) {
          activeMedia = viewer.querySelector('[data-media-id]:not([hidden])');
        }
        if (!activeMedia) activeMedia = viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (this.mediaPreview) {
          this.updatePreview(activeMedia);
        }

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.sliderGallery && typeof this.sliderGallery.reset === 'function') {
            this.sliderGallery.reset();
          }

          const mediaDots = this.querySelector('media-dots');
          if (mediaDots && typeof mediaDots.reset === 'function') {
            mediaDots.reset();
            if (typeof mediaDots.resetIndexes === 'function') mediaDots.resetIndexes();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          var smoothBehavior = this.motionReduced.matches ? 'auto' : 'smooth';
          var scrollList = activeMedia.closest('.product__media-list');
          if (scrollList && scrollList.scrollWidth > scrollList.clientWidth) {
            scrollList.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: smoothBehavior,
            });
            return;
          }
          var rect = activeMedia.getBoundingClientRect();
          if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;
          if (rect.bottom < 0) return;
          activeMedia.scrollIntoView({
            block: 'nearest',
            behavior: smoothBehavior,
          });
        });

        this.playActiveMedia(activeMedia);
        if (!this.mediaPreview) this._sampleZoomContrast(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((el) => el.removeAttribute('aria-current'));
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }

        var verticalSlider = thumbnail.closest('.thumbnail-slider');
        if (verticalSlider) {
          var thumbTop = thumbnail.offsetTop;
          var thumbBottom = thumbTop + thumbnail.offsetHeight;
          var scrollTop = verticalSlider.scrollTop;
          var viewHeight = verticalSlider.clientHeight;
          var smooth = this.motionReduced && this.motionReduced.matches ? 'auto' : 'smooth';
          if (thumbTop < scrollTop) {
            verticalSlider.scrollTo({ top: thumbTop, behavior: smooth });
          } else if (thumbBottom > scrollTop + viewHeight) {
            verticalSlider.scrollTo({ top: thumbBottom - viewHeight, behavior: smooth });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings?.imageAvailable?.replace('[index]', position) || '';
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;
        // Scope the "pause everything" sweep to the gallery's own subtree.
        // Dawn's `window.pauseAllMedia()` pauses every <video>/<product-model>
        // on the page, which also kills the sticky footer's 44x44 swatch
        // video (.dd-sticky-footer__swatch-video) and any other PDP video
        // that lives outside the gallery. For logged-in customers that was
        // hidden because the next variant:change kicked the sticky video
        // back on via updateSwatchPreview → _renderSwatchVideo → play().
        // Guests on non-gated PDPs don't get that kick (no product-form
        // hydrate, ATC replaced with login), so the sticky swatch sat
        // paused on the poster frame. Pausing only the gallery's own
        // slides preserves the "one gallery video at a time" contract
        // without reaching into unrelated sections.
        var viewer = this.sliderGallery || (this.elements && this.elements.viewer);
        if (viewer) {
          viewer.querySelectorAll('video').forEach(function (v) {
            if (typeof v.pause === 'function') v.pause();
          });
          viewer.querySelectorAll('.deferred-media').forEach(function (d) {
            if (typeof d.pause === 'function') d.pause();
          });
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);

        var self = this;
        setTimeout(function () {
          var viewer2 = self.sliderGallery || (self.elements && self.elements.viewer);
          if (viewer2) {
            viewer2.querySelectorAll('video').forEach(function (v) {
              v.play().catch(function () {});
            });
          }
          if (self.mediaPreview) {
            var pv = self.mediaPreview.querySelector('video');
            if (pv) pv.play().catch(function () {});
          }
        }, 0);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;
        const list = viewer.querySelector('ul, .product__media-list');
        if (list) {
          list.setAttribute('role', 'presentation');
          list.querySelectorAll('li').forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }

      updatePreview(activeMedia) {
        if (!this.mediaPreview) return;
        var mediaType = activeMedia.getAttribute('data-media-type') || 'image';
        this.mediaPreview.setAttribute('data-media-id', activeMedia.dataset.mediaId || '');
        this.mediaPreview.setAttribute('data-media-type', mediaType);

        if (mediaType === 'video' || mediaType === 'external_video') {
          var srcVideo = activeMedia.querySelector('video');
          if (!srcVideo) return;
          var existing = this.mediaPreview.querySelector('video');
          var existingImg = this.mediaPreview.querySelector('img');
          if (existingImg) existingImg.style.display = 'none';

          var targetVid;
          if (existing) {
            existing.poster = srcVideo.poster || '';
            existing.innerHTML = srcVideo.innerHTML;
            existing.load();
            existing.style.display = '';
            targetVid = existing;
          } else {
            targetVid = document.createElement('video');
            targetVid.className = 'product__media-video';
            targetVid.autoplay = true;
            targetVid.muted = true;
            targetVid.loop = true;
            targetVid.playsInline = true;
            targetVid.poster = srcVideo.poster || '';
            targetVid.innerHTML = srcVideo.innerHTML;
            this.mediaPreview.appendChild(targetVid);
          }

          targetVid.play().catch(function () {});
        } else {
          var img = activeMedia.querySelector('img');
          if (!img) return;
          var existingVid = this.mediaPreview.querySelector('video');
          if (existingVid) {
            existingVid.pause();
            existingVid.style.display = 'none';
          }

          var previewImg = this.mediaPreview.querySelector('img');
          if (previewImg) {
            previewImg.style.display = '';
            previewImg.src = img.src;
            previewImg.srcset = img.srcset || '';
            previewImg.alt = img.alt || '';
          }
        }

        if (this.zoomBtn) {
          this.zoomBtn.setAttribute('data-media-id', activeMedia.dataset.mediaId || '');
        }

        this._sampleZoomContrast();
      }

      _parseNicknameMap(raw) {
        var map = {};
        if (!raw) return map;
        raw.split(';').forEach(function (pair) {
          if (!pair) return;
          var parts = pair.split(':');
          if (parts.length >= 2) map[parts[0]] = parts.slice(1).join(':');
        });
        return map;
      }

      _onVariantChange(event) {
        var variant = event?.data?.variant || event?.detail?.variant;
        var variantId = variant?.id ? String(variant.id) : '';
        if (variantId) {
          this._applyVariantFilter(variantId, variant);
        }

        if (variantId) {
          var injectedVariantVideo = this.querySelector(
            `[data-dd-variant-video-for="${variantId}"][data-media-id]:not([hidden])`
          );
          if (injectedVariantVideo && injectedVariantVideo.dataset.mediaId) {
            this.setActiveMedia(injectedVariantVideo.dataset.mediaId, false);
          }
        }

        if (!this.badgeEl) return;

        var val = null;
        if (variant) {
          var optKey = 'option' + this.colorOptionIndex;
          val = variant[optKey];
        }

        if (!val) {
          var swatch = this.closest('product-info')?.querySelector('dd-swatch-selector .dd-swatch-group--color [data-dd-swatch-item].is--selected');
          if (swatch) val = swatch.dataset.value;
        }
        if (!val) return;

        if (this.badgeOptionEl) this.badgeOptionEl.textContent = val;

        var handle = val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        var nickname = this.nicknameMap[handle] || '';

        if (this.badgeNicknameEl) {
          this.badgeNicknameEl.textContent = nickname;
          this.badgeNicknameEl.style.display = nickname ? '' : 'none';
        }
        if (this.badgeSepEl) {
          this.badgeSepEl.style.display = nickname ? '' : 'none';
        }
      }

      _parseVariantMediaMap() {
        var pi = this.closest('product-info');
        var el = pi ? pi.querySelector('[data-dd-variant-media-map]') : null;
        if (!el) return new Map();
        try {
          var rows = JSON.parse(el.textContent);
          return new Map(rows.map(function (r) { return [String(r.id), r]; }));
        } catch (e) {
          return new Map();
        }
      }

      _filterInitialVariant() {
        if (this._variantMediaMap.size === 0) return;
        var variantId = this.dataset.initialVariant;
        if (!variantId) {
          var form = document.forms[this.getAttribute('form')];
          if (form) {
            var input = form.querySelector('[name="id"]');
            if (input && input.value) variantId = input.value;
          }
        }
        if (!variantId) return;
        this._applyVariantFilter(String(variantId));
      }

      _applyVariantFilter(variantId, variant) {
        if (this._variantMediaMap.size === 0) return;

        var entry = this._variantMediaMap.get(variantId);
        var ids = (entry && entry.media_ids && entry.media_ids.length) ? entry.media_ids : null;

        // Color-keyed inheritance fallback for variants absent from the map.
        //
        // The map is built from `product.variants` in Liquid, which Shopify
        // hard-caps at 250. On products with more than 250 variants (e.g.
        // SuperWeft = 1,056), variants beyond position 250 are simply not
        // in the map. Previous behavior was to fall back to
        // `[variant.featured_media.id]` — a single ID — which then hid the
        // other 3-of-4 legitimately-rendered metafield media items, giving
        // the appearance of a fallback render only after a variant CLICK
        // (direct-load works because `_filterInitialVariant` doesn't pass
        // the `variant` object so this branch ran `_filterToSingle(null)`
        // which keeps the first item — coincidentally fine when the
        // section was server-rendered with the right per-variant set).
        //
        // Better fallback: derive the variant's color from its option
        // values, gather every map entry sharing that color, and use the
        // union of their media_ids. Images are color-bound (not
        // length-bound) on these products, so any in-cap sibling of the
        // same color has the correct media set. If color can't be
        // resolved, do not filter — trust the section-rendered DOM, which
        // was already produced for this specific variant by the Section
        // Rendering API.
        if (!ids || !ids.length) {
          var inheritedIds = null;
          if (variant && this._variantMediaMap.size > 0) {
            var optVals = [variant.option1, variant.option2, variant.option3].filter(Boolean);
            var knownColors = new Set();
            this._variantMediaMap.forEach(function (e) {
              if (e.color) knownColors.add(e.color);
            });
            var matchedColor = optVals.find(function (v) { return knownColors.has(v); });
            if (matchedColor) {
              var siblingIds = [];
              this._variantMediaMap.forEach(function (e) {
                if (e.color === matchedColor && e.media_ids && e.media_ids.length) {
                  for (var i = 0; i < e.media_ids.length; i++) {
                    siblingIds.push(e.media_ids[i]);
                  }
                }
              });
              if (siblingIds.length > 0) inheritedIds = siblingIds;
            }
          }

          if (inheritedIds && inheritedIds.length) {
            ids = inheritedIds;
          } else {
            this._resetVisibility();
            return;
          }
        }

        var color = entry && entry.color;
        if (color) {
          var colorIds = [];
          this._variantMediaMap.forEach(function (e) {
            if (e.color === color && e.media_ids) {
              for (var i = 0; i < e.media_ids.length; i++) {
                colorIds.push(e.media_ids[i]);
              }
            }
          });
          if (colorIds.length > 0) ids = colorIds;
        }

        var visibleIds = new Set(ids.map(function (id) { return String(id); }));
        var viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;

        var mediaNodes = viewer.querySelectorAll('[data-media-id]');
        var hasVisible = false;
        var shownNormalized = new Set();

        mediaNodes.forEach(function (node) {
          if (node.closest('.product__preview')) return;
          var rawId = String(node.getAttribute('data-media-id') || '');
          var normalizedId = rawId.split('-').pop();
          var matchesId = visibleIds.has(rawId) || visibleIds.has(normalizedId);
          var show = matchesId && !shownNormalized.has(normalizedId);
          if (show) shownNormalized.add(normalizedId);
          node.toggleAttribute('hidden', !show);
          if (show) hasVisible = true;
        });

        if (!hasVisible) {
          this._filterToSingle(null);
          return;
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach(function (node) {
            var rawId = String(node.getAttribute('data-target') || '');
            var normalizedId = rawId.split('-').pop();
            var show = visibleIds.has(rawId) || visibleIds.has(normalizedId);
            node.toggleAttribute('hidden', !show);
          });
        }

        if (hasVisible) {
          if (this.sliderGallery && typeof this.sliderGallery.reset === 'function') {
            this.sliderGallery.reset();
          }
          var dots = this.querySelector('media-dots');
          if (dots && typeof dots.reset === 'function') {
            dots.reset();
            if (typeof dots.resetIndexes === 'function') dots.resetIndexes();
          }

          var firstVisible = viewer.querySelector('[data-media-id]:not([hidden]):not(.product__preview *)');
          if (firstVisible && firstVisible.dataset.mediaId) {
            this.setActiveMedia(firstVisible.dataset.mediaId, false);
          }
        }

        this._syncModalAttrs();
        this.countMediaGallery();
      }

      _resetVisibility() {
        // Un-hide every [data-media-id] in the gallery and thumbnails so
        // the gallery falls back to the full server-rendered DOM. Used
        // when the current variant is absent from the variantMediaMap
        // (cap >250) and no color-keyed sibling could be found — the
        // section was already rendered server-side for this exact
        // variant, so the existing DOM is correct.
        var viewer = this.sliderGallery || this.elements.viewer;
        if (viewer) {
          viewer.querySelectorAll('[data-media-id][hidden]').forEach(function (node) {
            if (node.closest('.product__preview')) return;
            node.removeAttribute('hidden');
          });
        }
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target][hidden]').forEach(function (node) {
            node.removeAttribute('hidden');
          });
        }
        if (this.sliderGallery && typeof this.sliderGallery.reset === 'function') {
          this.sliderGallery.reset();
        }
        var dots = this.querySelector('media-dots');
        if (dots && typeof dots.reset === 'function') {
          dots.reset();
          if (typeof dots.resetIndexes === 'function') dots.resetIndexes();
        }
        this.countMediaGallery();
      }

      _filterToSingle(featuredMediaId) {
        var viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;

        var items = viewer.querySelectorAll('[data-media-id]');
        var keepNode = null;

        if (featuredMediaId) {
          items.forEach(function (node) {
            if (node.closest('.product__preview')) return;
            var rawId = String(node.getAttribute('data-media-id') || '');
            var nId = rawId.split('-').pop();
            if (rawId === featuredMediaId || nId === featuredMediaId) keepNode = node;
          });
        }

        if (!keepNode) keepNode = viewer.querySelector('[data-media-id].is-active:not(.product__preview *)');
        if (!keepNode) keepNode = viewer.querySelector('[data-media-id]:not([hidden]):not(.product__preview *)');
        if (!keepNode) keepNode = viewer.querySelector('[data-media-id]:not(.product__preview *)');
        if (!keepNode) return;

        items.forEach(function (node) {
          if (node.closest('.product__preview')) return;
          node.toggleAttribute('hidden', node !== keepNode);
        });

        if (this.elements.thumbnails) {
          var keepId = keepNode.getAttribute('data-media-id');
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach(function (node) {
            node.toggleAttribute('hidden', node.getAttribute('data-target') !== keepId);
          });
        }

        keepNode.classList.add('is-active');
        if (this.mediaPreview) this.updatePreview(keepNode);

        if (this.sliderGallery && typeof this.sliderGallery.reset === 'function') {
          this.sliderGallery.reset();
        }
        var dots = this.querySelector('media-dots');
        if (dots && typeof dots.reset === 'function') {
          dots.reset();
          if (typeof dots.resetIndexes === 'function') dots.resetIndexes();
        }

        this._syncModalAttrs();
        this.countMediaGallery();
      }

      _sampleZoomContrast(activeMedia) {
        var wrapper = this.zoomBtn?.closest('.dd-gallery-card__zoom');
        if (!wrapper) return;

        var srcImg;
        if (this.mediaPreview) {
          srcImg = this.mediaPreview.querySelector('img');
        }
        if (!srcImg && activeMedia) {
          srcImg = activeMedia.querySelector('img');
        }
        if (!srcImg) {
          var viewer = this.sliderGallery || this.elements.viewer;
          if (viewer) {
            srcImg = viewer.querySelector('.is-active img, [data-media-id]:not([hidden]) img');
          }
        }
        if (!srcImg || !srcImg.src) return;

        var run = function (img) {
          var c = document.createElement('canvas');
          var s = 60;
          c.width = s;
          c.height = s;
          var ctx = c.getContext('2d');
          var sy = Math.max(0, img.naturalHeight - s);
          ctx.drawImage(img, 0, sy, s, s, 0, 0, s, s);
          try {
            var d = ctx.getImageData(0, 0, s, s).data;
            var sum = 0;
            for (var i = 0; i < d.length; i += 4) {
              sum += d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
            }
            // Mirror the same flag onto the gallery card so descendant overlays
            // (zoom button, star-rating chip, future bottom-left badges) can
            // all react to the bottom-region luminance from a single sample.
            // We keep the wrapper-level class for backward-compat with the
            // existing `.dd-gallery-card__zoom.--on-light` selectors.
            var isLight = sum / (d.length / 4) > 140;
            wrapper.classList.toggle('--on-light', isLight);
            var card = wrapper.closest('.dd-gallery-card');
            if (card) card.classList.toggle('--on-light', isLight);
          } catch (e) {}
        };

        var probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = function () { run(probe); };
        probe.onerror = function () {};
        var src = srcImg.currentSrc || srcImg.src;
        if (src.indexOf('width=') === -1) {
          src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=200';
        } else {
          src = src.replace(/width=\d+/, 'width=200');
        }
        probe.src = src;
      }

      _blockThumbnailModals() {
        if (!this.galleryCard) return;
        var self = this;
        this._xlMql = window.matchMedia('(min-width: 1280px)');
        this._mobileMql = window.matchMedia('(max-width: 1023px)');
        var viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;

        viewer.addEventListener('click', function (e) {
          if (!self._xlMql.matches) return;
          var item = e.target.closest('.product__media-item');
          if (!item) return;
          var mediaId = item.dataset.mediaId;
          if (!mediaId) return;
          var opener = e.target.closest('modal-opener');
          if (opener) {
            e.preventDefault();
            e.stopPropagation();
          }
          self.setActiveMedia(mediaId, false);
        }, true);

        this._syncModalAttrs();
        this._xlMql.addEventListener('change', () => this._syncModalAttrs());
        this._mobileMql.addEventListener('change', () => this._syncModalAttrs());
      }

      _syncModalAttrs() {
        var viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;
        var isXl = this._xlMql && this._xlMql.matches;
        var isMobile = this._mobileMql && this._mobileMql.matches;
        var disableModal = isXl || isMobile;
        viewer.querySelectorAll('modal-opener').forEach(function (opener) {
          if (disableModal) {
            if (!opener.dataset._savedModal) {
              opener.dataset._savedModal = opener.getAttribute('data-modal') || '';
            }
            opener.removeAttribute('data-modal');
          } else {
            if (opener.dataset._savedModal) {
              opener.setAttribute('data-modal', opener.dataset._savedModal);
            }
          }
        });
      }

      countMediaGallery() {
        const viewer = this.sliderGallery || this.elements.viewer;
        if (!viewer) return;

        const seenMedia = new Set();
        let mediaCount = 0;
        viewer.querySelectorAll('[data-media-id]').forEach((media) => {
          if (media.closest('.product__preview')) return;
          const rawId = String(media.getAttribute('data-media-id') || '');
          const normalizedId = rawId.split('-').pop();
          if (seenMedia.has(normalizedId)) return;
          if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
            seenMedia.add(normalizedId);
            mediaCount++;
          }
        });

        if (mediaCount > 1) {
          this.classList.remove('with-only1');
        } else {
          this.classList.add('with-only1');
        }
      }
    }
  );
}
